import React, { useEffect, useRef, useState } from 'react';
import './PitchDeck.css';
import { useScrollAnimations } from './ScrollAnimations';
import { useFullPageScroll } from './FullPageScroll';

function PitchDeck() {
  useScrollAnimations();
  useFullPageScroll();
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const mallVideoRef = useRef<HTMLVideoElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  useEffect(() => {
    // Force video to play on mobile
    const video = mobileVideoRef.current;
    if (video) {
      video.play().catch(err => {
        console.log('Video autoplay failed:', err);
        // Try to play on first user interaction
        document.addEventListener('touchstart', () => {
          video.play();
        }, { once: true });
      });
    }
  }, []);
  
  useEffect(() => {
    // Set up Intersection Observer for mall video
    const mallVideo = mallVideoRef.current;
    if (!mallVideo) return;
    
    // Try to play immediately for tablet/mobile
    const playVideo = () => {
      mallVideo.play().catch(err => {
        console.log('Mall video autoplay failed:', err);
      });
    };
    
    // Try multiple times to ensure playback
    playVideo();
    setTimeout(playVideo, 500);
    setTimeout(playVideo, 1000);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Reset and play video when section comes into view
            mallVideo.currentTime = 0;
            mallVideo.play().catch(err => {
              console.log('Mall video play failed:', err);
            });
          } else {
            // Pause video when section leaves view
            mallVideo.pause();
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of the section is visible
    );
    
    // Observe the use-cases section
    const useCasesSection = document.getElementById('use-cases');
    if (useCasesSection) {
      observer.observe(useCasesSection);
    }
    
    return () => {
      if (useCasesSection) {
        observer.unobserve(useCasesSection);
      }
    };
  }, []);

  const downloadPDF = () => {
    setIsDownloading(true);
    
    // Convert Google Drive view link to direct download link
    const googleDriveId = '1HiHXRG-ZLE4AttS1q86106vqYi1f0VtS';
    const directDownloadUrl = `https://drive.google.com/uc?export=download&id=${googleDriveId}`;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = directDownloadUrl;
    link.download = 'Herba-Pitch-Deck.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Reset loading state after a delay
    setTimeout(() => setIsDownloading(false), 3000);
  };
  
  return (
    <div className="pitch-deck">
      <nav className="navbar">
        <div className="nav-container">
          <img src="/images/logo.png" alt="HERBA" className="logo-image" />
          <ul className="nav-links">
            <li><button onClick={downloadPDF} className="nav-cta" disabled={isDownloading}>
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </button></li>
          </ul>
        </div>
      </nav>
      
      <header id="hero" className="hero-section pitch-hero-section">
        <div className="hero-content">
          <div className="hero-text">
            {/* <h2 className="hero-title">Sensor Based<br />Smart Landscaping</h2> */}
            <h2 className="hero-title">Commercial Landscaping Care Reimagined</h2>

            <div className="hero-subtitle">
              {/* <p><strong>Modernizing a Legacy Industry + <br></br>Ensuring & assuring your green investments are protected</strong></p> */}
              <p><strong>Sensors that ensure & assure green investments are protected</strong></p>

              <ul className="hero-checklist">
                <li>Industry Buy-In</li>
                <li>Successful Pilot</li>
                <li>Sales Partnerships</li>
              </ul>
              <p><strong>Join our seed round</strong></p>
            </div>
          </div>
          <div className="hero-visual">
            <div className="sensor-container">
              <div className="cell-pulses">
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
              </div>
              <img src="/images/sensor.png" alt="Herba Smart Sensor" className="sensor-image" />
            </div>
          </div>
        </div>
      </header>
      
      <div className="video-overlay">
        <video 
          className="scroll-video"
          src="/images/sensor-scroll.mp4"
          muted
          playsInline
          preload="auto"
          // style={{ borderRadius: '12px' }}
        />
      </div>
      
      <nav className="section-nav">
        <div className="nav-dot active" data-section="hero">
          <span className="nav-tooltip">Home</span>
        </div>
        <div className="nav-dot" data-section="technology">
          <span className="nav-tooltip">Why Now</span>
        </div>
        <div className="nav-dot" data-section="use-cases">
          <span className="nav-tooltip">Use Cases</span>
        </div>
        <div className="nav-dot" data-section="dashboard">
          <span className="nav-tooltip">Product Features</span>
        </div>
        <div className="nav-dot" data-section="why-now">
          <span className="nav-tooltip">Market Opportunity</span>
        </div>
        <div className="nav-dot" data-section="business-model">
          <span className="nav-tooltip">Business Model & Projections</span>
        </div>
        <div className="nav-dot" data-section="why-us">
          <span className="nav-tooltip">Traction + Wins</span>
        </div>
        <div className="nav-dot" data-section="seed-round">
          <span className="nav-tooltip">Funding</span>
        </div>
        <div className="nav-dot" data-section="team">
          <span className="nav-tooltip">Team</span>
        </div>
        <div className="nav-dot" data-section="timeline">
          <span className="nav-tooltip">Timeline</span>
        </div>
        <div className="nav-dot" data-section="contact">
          <span className="nav-tooltip">Contact</span>
        </div>
      </nav>
      
      <main className="content-wrapper">
        <div className="hero-spacer"></div>
        
        <section id="technology" className="technology-section">
          <div className="tech-layout">
            <div className="tech-content">
              {/* <h2 className="section-title fade-up">Patented Sensor Technology</h2> */}
              {/* <h2 className="section-title fade-up">Take the guesswork out of plant care</h2> */}
              {/* <h2 className="section-title fade-up">Taking commercial landscaping to the next level</h2> */}
              <h2 className="section-title fade-up">Why Now?</h2>


              {/* <p className="section-subtitle fade-up">Rethinking commercial landscaping.</p> */}
              <div className="tech-description fade-up">
                <div className="tech-stats-list" style={{ marginTop: '1.5rem' }}>
                  <div className="tech-stat-item">
                    <span className="stat-number">62%</span>
                    <span className="stat-text">of landscape companies struggle to hire crews</span>
                  </div>
                  {/* <div className="tech-stat-item">
                    <span className="stat-number">30%</span>
                    <span className="stat-text">of companies are turning to automation to fill gaps</span>
                  </div> */}
                </div>
                {/* <h3><br></br>Herba = Healthy plants with less labor; eliminates human error and ensures maintenance is handled properly.<br></br>  Herba protects the massive investment spaces make in landscaping, eliminates human error and ensures maintenance is handled properly.</h3> */}
                {/* <h3><br></br>Herba = Healthy plants with less labor, keeping spaces effortlessly green.</h3> */}
                <h3>Plants don't care about labor shortages.<br />Herba protects your plants and budget.</h3>

                {/* <h2 className="section-title fade-up" style={{ marginTop: '2.5rem' }}>Herba reduces the need for manual labor, keeping spaces green</h2> */}
                <div className="tech-stats-list" style={{ marginTop: '1.5rem' }}>
                <div className="tech-stat-item">
                    <span className="stat-number">50%</span>
                    <span className="stat-text">reduction in water usage</span>
                  </div>
                  <div className="tech-stat-item">
                    <span className="stat-number">25-50%</span>
                    <span className="stat-text">fewer maintenance visits needed</span>
                  </div>
                  <div className="tech-stat-item">
                    <span className="stat-number">100%</span>
                    <span className="stat-text">visibility into plant health</span>
                  </div>
{/* 
                  <div className="tech-stat-item">
                    <span className="stat-number">25%</span>
                    <span className="stat-text">reduction in time on site</span>
                  </div> */}
                </div>
              </div>
            </div>
            {/* <div className="tech-spacer"></div> */}
            <div className="tech-video-mobile">
              <video 
                ref={mobileVideoRef}
                className="scroll-video-mobile"
                src="/images/sensor-scroll.mp4"
                muted
                playsInline
                preload="auto"
                autoPlay
                loop
                controls={false}
              />
            </div>
          </div>
        </section>
        
        <section id="use-cases" className="use-cases-section">
          <div className="container">
            <div className="use-cases-layout">
              <div className="use-cases-image">
                <video 
                  ref={mallVideoRef}
                  src="/images/mall.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="use-cases-content">
                {/* <h2 className="section-title">the most versatile configurable system for reducing maintenance</h2> */}
                                <h2 className="section-title">Sensor-Based Care Designed to Save Water, Automate and Protect</h2>

                {/* <p className="section-subtitle">Herba combines state-of-the-art sensors and modular water reservoirs to automate plant care where traditional irrigation isn't possible.</p> */}
                <p className="section-subtitle">Smart sensors + water delivery that automate plant care beyond traditional irrigation</p>                <div className="use-cases-grid">
                  <div className="use-case-card fade-up use-case-flex-card">
                    
                    <div className="use-case-stat-section">
                      <h3>Ideal For</h3>
                      <ul>
                        <li>Exterior landscaping</li>
                        <li>Drought-prone regions, areas with limited water access</li>
                      </ul>
                    </div>
                    <div className="use-case-info-section">
                      <h3>Target Markets / Segments</h3>
                      <ul>
                        <li>Malls, hotels, offices, retail chains, municipalities, public spaces, schools, government facilities</li>
                        <li>Development, real estate, architecture & investment firms</li>
                      </ul>
                    </div>
                  </div>
                              {/* <div className="use-case-card fade-up">
                <h3>Why?</h3>
                <ul>
                  <li>Post-pandemic hiring issues</li>
                  <li>Growing adoption of automation</li>
                  <li>Increasing spend to get people back into offices</li>

                </ul>
              </div> */}
              {/* <div className="use-case-card fade-up">
                <h3>Target Market</h3>
                <ul>
                  <li>Commercial facilities</li>
                  <li>Real estate investment firms</li>
                  <li>Corporate development firms</li>
                  <li>Architecture and design firms</li>
                </ul>
              </div> */}
              {/* <div className="use-case-card fade-up">
                <h3>Agriculture</h3>
                <ul>
                  <li>Urban farming</li>
                  <li>Greenhouses</li>
                  <li>Research facilities</li>
                </ul>
              </div> */}
            </div>
              </div>
            </div>
          </div>
        </section>
        
        <section id="dashboard" className="dashboard-section" style={{ display: 'flex', alignItems: 'center', minHeight: '100vh' }}>
          <div className="dashboard-background">
            <img src="/images/dahboard_bgr.png" alt="Dashboard Background" className="dashboard-bg-img" />
          </div>
          <div className="dashboard-content" style={{ display: 'flex', alignItems: 'center', height: '100vh' }}>
            <div className="dashboard-info">
              <h2 className="section-title fade-up">Herba Product Features</h2>
              <div className="dashboard-features">
                <div className="dashboard-feature fade-up">
                  <h3>Real-Time Monitoring</h3>
                  <ul style={{ paddingLeft: '0', marginBottom: '0.8rem', lineHeight: '1.6' }}>
                  {/* <li style={{ marginBottom: '0.8rem' }}>Watering need alerts</li> */}
                    <li style={{ marginBottom: '0.8rem' }}>Automated watering + water level monitoring (optional irrigation add on)</li>
                    <li style={{ marginBottom: '0.8rem' }}>Dashboard access & notifications about lapses in maintenance</li>
                    <li style={{ marginBottom: '0.8rem' }}>Multi-location management</li>
                    <li style={{ marginBottom: '0.8rem' }}>Cell, Wi-Fi & GPS enabled</li>
                    <li style={{ marginBottom: '0.8rem' }}>Solar charging</li>
                  </ul>
                </div>
                {/* <div className="dashboard-feature dashboard-benefits fade-up">
                  <h3>Benefits</h3>
                  <ul style={{ paddingLeft: '0', marginBottom: '0.8rem', lineHeight: '1.6' }}>
                    <li style={{ marginBottom: '0.8rem' }}>Reduces water consumption by 50%</li>
                    <li style={{ marginBottom: '0.8rem' }}>Reduces redundant visits and time on site</li>
                    <li style={{ marginBottom: '0.8rem' }}>Fewer technicians needed</li>
                    <li style={{ marginBottom: '0' }}>Maintenance alerts & reminders</li>
                  </ul>
                </div> */}
              </div>
            </div>
            <div className="dashboard-visual">
              <img src="/images/dashboard.png" alt="Dashboard Interface" className="dashboard-img slide-up" />
              <img src="/images/dashboard_mobile.png" alt="Dashboard Mobile" className="dashboard-mobile-img" />
            </div>
          </div>
        </section>
        
        <section id="why-now" className="why-now-section">
          <div className="container">
            <h2 className="section-title fade-up">Market Opportunity</h2>
            <div className="market-showcase">
              <div className="total-market fade-up">
                <div className="market-number">$105B</div>
                <p className="market-label">U.S. Commercial Landscaping Market</p>
              </div>
              {/* <div className="market-breakdown fade-up">
                <div className="breakdown-item">
                  <div className="breakdown-bar" style={{width: '70%'}}>
                    <span className="breakdown-value">$73B</span>
                  </div>
                  <p>Maintenance & Services</p>
                </div>
              </div> */}
              <div className="market-stats-row fade-up">
              <div className="market-stat">
                  <h3>$73B</h3>
                  <p>Maintenance & Services</p>
                </div>
                <div className="market-stat">
                  <h3>9.5B</h3>
                  <p>Square feet of commercial landscape</p>
                </div>
                <div className="market-stat">
                  <h3>62%</h3>
                  <p>Companies struggling to hire</p>
                </div>
                <div className="market-stat">
                  <h3>30%</h3>
                  <p>Annual labor cost increase</p>
                </div>
              </div>
              <div className="market-opportunity-text fade-up">
                <h3>Addressable Market</h3>
                <div className="opportunity-value">$2.1B</div>
                <p style={{marginTop: '8px', fontSize: '1.1em'}}>Monitoring + Smart irrigation solutions for commercial properties</p>
              </div>
            </div>
          </div>
        </section>
        
        <section id="business-model" className="business-model-section">
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="section-title fade-up" style={{ marginBottom: '3rem' }}>Business Model & Projections</h2>
            <div className="business-projections-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '4rem', maxWidth: '1000px', margin: '0 auto' }}>
              <div className="revenue-streams fade-up" style={{ flex: '1', textAlign: 'left' }}>
                <h3 style={{ color: '#00c853', marginBottom: '1rem' }}>Revenue Streams</h3>
                <ul>
                <li><strong>The initial 500 units will generate over $125,000 in 18 months</strong></li>

                  <li><strong>Hardware Sales:</strong> One-time initial sale ($375)</li>
                  <li><strong>Gross margin on Hardware:</strong> 2.5x</li>
                  <li><strong>Annual SaaS Subscription:</strong> $60 per device</li>
                  <li><strong>Gross margin on Software:</strong> 2x</li>
                </ul>
              </div>
              <div className="financial-projections fade-up" style={{ flex: '1.5', minWidth: '500px' }}>
                <h3 style={{ color: '#00c853', marginBottom: '1rem', textAlign: 'left' }}>Revenue Projections</h3>
                <div className="projection-visual" style={{ height: '300px', marginBottom: '2rem' }}>
                  <div className="projection-year">
                    <div className="year-label" style={{ fontSize: '1.1rem' }}>Year 1</div>
                    <div className="revenue-bar" style={{ height: '30px' }}></div>
                    <div className="revenue-amount" style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>-$330K</div>
                    <div className="units-amount" style={{ fontSize: '0.9rem' }}>5,000 units</div>
                  </div>
                  <div className="projection-year">
                    <div className="year-label" style={{ fontSize: '1.1rem' }}>Year 2</div>
                    <div className="revenue-bar" style={{ height: '120px' }}></div>
                    <div className="revenue-amount" style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>$1.2M</div>
                    <div className="units-amount" style={{ fontSize: '0.9rem' }}>11,000 units</div>
                  </div>
                  <div className="projection-year">
                    <div className="year-label" style={{ fontSize: '1.1rem' }}>Year 3</div>
                    <div className="revenue-bar" style={{ height: '280px' }}></div>
                    <div className="revenue-amount" style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>$3.4M</div>
                    <div className="units-amount" style={{ fontSize: '0.9rem' }}>14,000 units</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section id="why-us" className="why-us-section">
          <div className="container">
            <h2 className="section-title fade-up">Traction and Wins</h2>
            <div className="why-us-grid">
              <div className="why-us-box fade-up">
                <h3>3 Years</h3>
                <p>of R&D working with leading scientists and developers to take the guesswork out of plant care.</p>
              </div>
              <div className="why-us-box fade-up">
                <h3>$400K</h3>
                <p>Raised in pre-seed funding from industry leaders. $300K from a customer funding our development.</p>
              </div>
              <div className="why-us-box fade-up">
                <h3>Launched</h3>
                <p>A successful pilot with DVT units with 95% success rate.</p>
              </div>
              <div className="why-us-box fade-up">
                <h3>Partnerships</h3>
                <p>Sales partnership with one of the largest manufacturers/sellers in the commercial landscaping space</p>
              </div>
              <div className="why-us-box fade-up">
                <h3>LOIs</h3>
                <p>5,000 Unit LOI from largest planter importer/seller in the U.S.</p>
              </div>
              <div className="why-us-box fade-up">
                <h3>Patent</h3>
                <p>Patent approved, the product is fully protected.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* <section id="features" className="features-section">
          <div className="container">
            <h2 className="section-title fade-up">Smart Features</h2>
            <div className="features-grid">
              <div className="feature-card fade-up">
                <h3>Automated Watering</h3>
                <p>Sub-irrigation technology delivers water directly to roots for healthier plants and less waste, with adjustable settings if needed</p>
              </div>
              <div className="feature-card fade-up">
                <h3>Dashboard Access</h3>
                <p>Intuitive dashboard provides real-time insights and control, monitoring water levels, environmental conditions, and plant health from anywhere</p>
              </div>
              <div className="feature-card fade-up">
                <h3>Solar Charging</h3>
                <p>Years of battery life powered by solar energy, keeping your system running without interruption</p>
              </div>
              <div className="feature-card fade-up">
                <h3>Fits In Any Pot</h3>
                <p>Our versatile modular watering system adapts to a variety of settings and locations</p>
              </div>
              <div className="feature-card fade-up">
                <h3>GPS & Fleet Management</h3>
                <p>For businesses, real-time GPS and cellular/WiFi tracking help you efficiently manage multiple sites and optimize your operations</p>
              </div>
              <div className="feature-card fade-up">
                <h3>Notifications</h3>
                <p>Get timely alerts about water levels, plant health, and maintenance needs delivered straight to your email</p>
              </div>
            </div>
          </div>
        </section> */}
        
        <section id="seed-round" className="seed-round-section">
          <div className="container">
            <h2 className="section-title fade-up">Seed Round</h2>
            <div className="raise-details fade-up">
              <div className="raise-amount">
                <h3>Raising $1.5M</h3>
                <p>Purchase inventory, accelerate growth, <br></br>& provide 2 years runway</p>
                
                <div className="investor-preference" style={{ marginTop: '1.5rem' }}>
                  <h3>Looking for Strategic Partners</h3>
                  <p>We are seeking investments from partners in the property tech, real estate, and corporate development sectors who can provide strategic value beyond capital.</p>
                </div>
              </div>
              <div className="raise-info">
                <div className="use-of-funds">
                  <h3>Use of Funds</h3>
                  <ul>
                    <li><strong>40%</strong> - Product manufacturing & inventory</li>
                    <li><strong>30%</strong> - Team expansion (sales + customer support)</li>
                    <li><strong>20%</strong> - Sales & marketing</li>
                    <li><strong>10%</strong> - Operations & working capital</li>
                  </ul>
                </div>
                <div className="investor-benefits">
                  <h3>Why Invest Now</h3>
                  <ul>
                  <li>Sales partnership with leading manufacturer</li>
                  <li>Proven technology</li>

                    {/* <li>Proven technology with patent protection</li> */}
                    <li>Massive and growing market opportunity</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section id="team" className="team-section">
          <div className="container">
            <h2 className="section-title fade-up">Team & Investors</h2>
            <div className="team-content">
              <div className="team-wrapper fade-up">
                <div className="team-grid">
                  <div className="team-member">
                    <img src="/images/kasey_team.png" alt="Kacey Fillingim" className="team-photo" />
                    <h5>Kacey Fillingim</h5>
                    <h4>Founder + CEO</h4>
                    <p className="team-bio">On Deck + Techstars Catalyst Alum; A product design leader with over a decade of experience in B2B and consumer software.</p>
                    <div className="team-social">
                      <a href="mailto:kacey@herba-grown.com?subject=Partnership Inquiry&body=Hi Kacey,%0D%0A%0D%0AI'm interested in learning more about Herba.%0D%0A%0D%0ABest regards," className="social-icon" title="Email Kacey">
                        📧
                      </a>
                      <a href="https://www.linkedin.com/in/kaceyfillingim/" target="_blank" rel="noopener noreferrer" className="social-icon" title="LinkedIn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                  <div className="team-member">
                    <img src="/images/sivan_team.png" alt="Sivan Sud" className="team-photo" />
                    <h5>Sivan Sud</h5>
                    <h4>Advisor: Engineering</h4>
                    <p className="team-bio">Sivan co-founded and led engineering teams in the AgTech space. He holds a Master's degree from Cornell in mechanical engineering.</p>
                    <div className="team-social">
                      <a href="https://www.linkedin.com/in/ssud/" target="_blank" rel="noopener noreferrer" className="social-icon" title="LinkedIn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                  <div className="team-member">
                    <img src="/images/drrob_team.png" alt="Dr. Robert Flannery" className="team-photo" />
                    <h5>Dr. Robert Flannery</h5>
                    <h4>Advisor: Plant science</h4>
                    <p className="team-bio">Dr. Flannery pioneered early IoT plant sensors and is the CEO of Dr. Robb farms. He holds a PhD from UC Davis in Plant Biology.</p>
                    <div className="team-social">
                      <a href="https://www.linkedin.com/in/robertflannery/" target="_blank" rel="noopener noreferrer" className="social-icon" title="LinkedIn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="investors-section">
                  <h3>Investors Include</h3>
                  <div className="investors-grid">
                    <div className="investor">
                      <h4>Lead Investor</h4>
                      <p>Major Commercial Landscaping Manufacturer</p>
                    </div>
                    <div className="investor">
                      <h4>Strategic Partner</h4>
                      <p>One of the largest planter distributors in the U.S.</p>
                    </div>
                    <div className="investor">
                      <h4>Angel Investor</h4>
                      <p>Former CPO of Lyft</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <section id="timeline" className="timeline-section">
          <div className="container" style={{ paddingTop: '80px' }}>
            <h2 className="section-title fade-up">Timeline & Milestones</h2>
            <div className="timeline-wrapper" style={{ display: 'flex', gap: '3rem', alignItems: 'flex-start' }}>
              <div className="timeline-image" style={{ flex: '0 0 35%', marginTop: '2rem' }}>
                <img src="/images/landscaping_pitch.png" alt="Landscaping" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
              </div>
              <div className="timeline fade-up" style={{ flex: '1' }}>
                <div className="timeline-item">
                <div className="timeline-date">CURRENT</div>
                <div className="timeline-content">
                  <h4>Testing DVT units</h4>
                  <p>Launched 30-unit pilot with partners</p>
                </div>
              </div>
              
                <div className="timeline-item">
                <div className="timeline-date">Q4 2025</div>
                <div className="timeline-content">
                  <h4>Beta launch</h4>
                  <p>Launch Beta with 30 customers</p>
                  <p>UXR, make any final changes to DVT for PVT</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-date">Q1 2026</div>
                <div className="timeline-content">
                  <h4>Manufacturing</h4>
                  <p>Place initial PVT order</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-date">Q2 2026</div>
                <div className="timeline-content">
                  <h4>Product Launch</h4>
                  <p>First units shipped to early customers</p>
                  <p>Hire team to prepare for Q2 scaling</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-date">Q2 2026</div>
                <div className="timeline-content">
                  <h4>Scaling</h4>
                  <p>Dialing in manufacturing, sales, and partnerships</p>
                </div>
              </div>
              {/* <div className="timeline-item">
                <div className="timeline-date">Q1 2027</div>
                <div className="timeline-content">
                  <h4>Series A Preparation</h4>
                </div>
              </div> */}
            </div>
            </div>
          </div>
        </section>
        
        <section id="contact" className="contact-section">
          <div className="container">
            <h2 className="section-title fade-up">Interested in joining our seed round?</h2>
            <div className="contact-content fade-up">
              <p className="contact-subtitle">Let's discuss creating the future of commercial landscaping.</p>
              <button 
                onClick={() => {
                  const subject = encodeURIComponent('Seed Funding Inquiry');
                  const body = encodeURIComponent('Hi Kacey,\n\nI\'m interested in learning more about Herba and potential partnership opportunities.\n\nBest regards,');
                  window.location.href = `mailto:kacey@herba-grown.com?subject=${subject}&body=${body}`;
                }}
                className="contact-button"
              >
                Contact Kacey
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default PitchDeck;